import { InfoCircleIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Ellipsis } from '@prophecy/ui-v3/Ellipsis';
import { Stack } from '@prophecy/ui-v3/Layout';
import { Tooltip } from '@prophecy/ui-v3/Tooltip';
import { Text } from '@prophecy/ui-v3/Typography';
import { ReactNode } from 'react';
import styled from 'styled-components';

export type KeyValPair = Record<string, { value?: ReactNode; info?: string }>;

const Card = styled.div`
  border: 1px solid ${theme.colors.gray200};
  border-radius: ${theme.radius.x8};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const Header = styled.div`
  padding: ${theme.spaces.x10} ${theme.spaces.x16};
  background: ${theme.colors.gray50};
  border-bottom: 1px solid ${theme.colors.gray200};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr);
  column-gap: ${theme.spaces.x16};
  align-items: center;
  min-height: 40px;
  padding: ${theme.spaces.x8} ${theme.spaces.x16};

  & + & {
    border-top: 1px solid ${theme.colors.gray100};
  }
`;

const Value = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const InfoIconWrapper = styled.span`
  display: inline-flex;
  cursor: help;
`;

function toTestId(text: string) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

export function EmptyValue() {
  return (
    <Text level='sm' tone={theme.colors.gray400}>
      —
    </Text>
  );
}

export function KeyValuePair({
  header,
  keyValPair,
  'data-test-id': testId = `key-value-pair-${toTestId(header)}`
}: {
  header: string;
  keyValPair: KeyValPair;
  'data-test-id'?: string;
}) {
  return (
    <Card data-test-id={testId}>
      <Header>
        <Text level='sm' weight={theme.fontWeight.semiBold} tone={theme.colors.gray700}>
          {header}
        </Text>
      </Header>
      {Object.entries(keyValPair).map(([key, { value, info }]) => {
        const rowTestId = `${testId}-${toTestId(key)}`;
        const isEmpty = value === undefined || value === null || value === '';
        return (
          <Row key={key}>
            <Stack direction='horizontal' gap={theme.spaces.x4} alignY='center'>
              <Text level='sm' tone={theme.colors.gray500}>
                {key}
              </Text>
              {info && (
                <Tooltip title={info}>
                  <InfoIconWrapper data-test-id={`${rowTestId}-info`}>
                    <InfoCircleIcon type='default' size='xs' color={theme.colors.gray400} />
                  </InfoIconWrapper>
                </Tooltip>
              )}
            </Stack>
            <Value data-test-id={rowTestId}>
              {isEmpty ? (
                <EmptyValue />
              ) : typeof value === 'string' || typeof value === 'number' ? (
                <Ellipsis tooltip>
                  <Text level='sm' tone={theme.colors.gray900}>
                    {value}
                  </Text>
                </Ellipsis>
              ) : (
                value
              )}
            </Value>
          </Row>
        );
      })}
    </Card>
  );
}
